/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeSectionToolbar.tsx
 * FILE_ID: QB-AGGRID-TOOLBAR-001
 * PURPOSE: SAM-style toolbar that sits directly above each section's
 *          AG-Grid: density toggle, reset widths, add row / add section,
 *          and client-labels toggle.
 * WHY: The estimator screenshot showed SAM's grid framed by a clean
 *      pill-shaped toolbar (Comfortable | Compact, Reset widths, More
 *      settings, + Add row, + Lump sum, + Add section, Client labels).
 *      Quotech had no toolbar — section management was a single
 *      collapsible header strip with no quick actions. This component
 *      closes that gap.
 * FLOW:
 *   ┌──────────────────────┐    ┌──────────────────────┐    ┌──────────────────────┐
 *   │ density / clientView │ -> │ user click           │ -> │ onDensityChange /    │
 *   │ state (parent)       │    │                      │    │ onAddRow / etc.      │
 *   └──────────────────────┘    └──────────────────────┘    └──────────────────────┘
 * DEPENDENCIES: lucide-react icons; tailwind utility classes.
 * RELATED:
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection.tsx) — consumer
 *   - QB-AGGRID-BANNER-001 (ScopeClientViewBanner.tsx) — sits below toolbar
 */

import { Plus, RefreshCw, Eye, Settings2, DollarSign, Package } from 'lucide-react'
import { getSectionColor } from './section-color-palette'

export type ScopeGridDensity = 'comfortable' | 'compact'

export interface ScopeSectionToolbarProps {
  /**
   * Stable section identifier. Used to pick a deterministic colour
   * band from SECTION_COLOR_PALETTE so the toolbar renders as a
   * SAM-style full-width section banner. Optional for backwards
   * compatibility — empty/missing falls back to slot 0 (amber).
   */
  sectionId?: string
  sectionTitle: string
  itemCount: number
  sectionSubtotal?: string | number | null
  density: ScopeGridDensity
  onDensityChange: (next: ScopeGridDensity) => void
  isEditable: boolean
  onResetWidths: () => void
  onAddRow: () => void
  onAddSection: () => void
  /**
   * SAM-style "+ Lump sum" peer of onAddRow. Seeds a row with
   * quantity=1, unit="lump sum", manual pricing mode so the
   * total is whatever the estimator types into override_chargeout.
   */
  onAddLumpSum?: () => void
  /**
   * SAM-style "+ Pricelist" — opens the pricebook picker modal so
   * the estimator can pick a material and prefill description/unit
   * /rate on the focused (or topmost pad) row.
   */
  onOpenPricelist?: () => void
  onToggleClientLabels: () => void
  isClientLabelsOn: boolean
}

function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '$0.00'
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value))
  if (!Number.isFinite(n)) return '$0.00'
  return n.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export default function ScopeSectionToolbar({
  sectionId,
  sectionTitle,
  itemCount,
  sectionSubtotal,
  density,
  onDensityChange,
  isEditable,
  onResetWidths,
  onAddRow,
  onAddSection,
  onAddLumpSum,
  onOpenPricelist,
  onToggleClientLabels,
  isClientLabelsOn,
}: ScopeSectionToolbarProps) {
  const summary = `${itemCount} ${itemCount === 1 ? 'line' : 'lines'} · ${formatCurrency(sectionSubtotal)}`
  // SAM-style full-width section banner: deterministic colour band
  // derived from the section id. Same id → same colour across reloads.
  const band = getSectionColor(sectionId)

  return (
    <div
      data-section-color={band.name}
      className={`flex flex-wrap items-center gap-2 px-3 py-1.5 border-l-4 ${band.bannerBorder} ${band.bannerBg} rounded-r-md`}
    >
      {/* Section name + running summary — left-aligned, SAM style. */}
      <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
        <span
          className={`text-sm font-semibold tracking-wide uppercase ${band.titleText}`}
        >
          {sectionTitle}
        </span>
        <span className="text-[11px] text-slate-500 dark:text-slate-400 whitespace-nowrap">
          {summary}
        </span>
      </div>

      <div className="flex-1" />

      {/* Density toggle — pill, SAM's signature control. */}
      <div className="inline-flex items-center rounded-full border border-slate-300 dark:border-slate-600 p-0.5 text-[11px] bg-white dark:bg-slate-900">
        <button
          type="button"
          onClick={() => onDensityChange('comfortable')}
          className={`px-2.5 py-0.5 rounded-full transition-colors ${
            density === 'comfortable'
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-medium'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          Comfortable
        </button>
        <button
          type="button"
          onClick={() => onDensityChange('compact')}
          className={`px-2.5 py-0.5 rounded-full transition-colors ${
            density === 'compact'
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-medium'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          Compact
        </button>
      </div>

      <button
        type="button"
        onClick={onResetWidths}
        title="Reset all column widths to their defaults"
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <RefreshCw className="w-3 h-3" />
        Reset widths
      </button>

      <button
        type="button"
        title="More settings (coming soon — opens Setup picker today)"
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <Settings2 className="w-3 h-3" />
        More settings
      </button>

      <div className="h-4 w-px bg-slate-300 dark:bg-slate-600 mx-0.5" />

      {isEditable && (
        <>
          <button
            type="button"
            onClick={onAddRow}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-emerald-500/40 text-[11px] text-emerald-700 dark:text-emerald-400 bg-white dark:bg-slate-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
          >
            <Plus className="w-3 h-3" />
            Add row
          </button>
          {onAddLumpSum && (
            <button
              type="button"
              onClick={onAddLumpSum}
              title="Add a non-itemised lump-sum row (e.g. Mobilisation, Permit fee)"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-amber-500/40 text-[11px] text-amber-700 dark:text-amber-400 bg-white dark:bg-slate-900 hover:bg-amber-50 dark:hover:bg-amber-950/40"
            >
              <DollarSign className="w-3 h-3" />
              Lump sum
            </button>
          )}
          {onOpenPricelist && (
            <button
              type="button"
              onClick={onOpenPricelist}
              title="Pick a material from the pricelist (auto-fills description, unit, rate)"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-sky-500/40 text-[11px] text-sky-700 dark:text-sky-400 bg-white dark:bg-slate-900 hover:bg-sky-50 dark:hover:bg-sky-950/40"
            >
              <Package className="w-3 h-3" />
              Pricelist
            </button>
          )}
          <button
            type="button"
            onClick={onAddSection}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-emerald-500/40 text-[11px] text-emerald-700 dark:text-emerald-400 bg-white dark:bg-slate-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
          >
            <Plus className="w-3 h-3" />
            Add section
          </button>
        </>
      )}

      <button
        type="button"
        onClick={onToggleClientLabels}
        title={
          isClientLabelsOn
            ? 'Hide client-visible column highlight'
            : 'Highlight columns that appear on the client PDF'
        }
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
          isClientLabelsOn
            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
        }`}
      >
        <Eye className="w-3 h-3" />
        Client labels
      </button>
    </div>
  )
}
